import type { MemberUser, StampHistoryItem } from "./types";
import { apiUrl, effectiveApiBase } from "./apiBase";

const jsonHeaders = { "Content-Type": "application/json" };

/** WKWebView 等で到達不能ホストへ fetch すると長時間ハングしうるため上限を付与 */
const API_FETCH_TIMEOUT_MS = 20_000;
const HEALTH_FETCH_TIMEOUT_MS = 4_000;

function isLikelyNetworkFailure(e: unknown): boolean {
  return (
    e instanceof TypeError &&
    (e.message === "Failed to fetch" ||
      e.message.includes("fetch") ||
      e.message.includes("NetworkError") ||
      e.message.includes("NETWORK"))
  );
}

function apiConnectionHint(): string {
  const configured = (import.meta.env.VITE_API_BASE_URL ?? "").trim();
  const viaProxy = !effectiveApiBase();
  return [
    "API（Express・通常はポート 3001）に接続できません。",
    viaProxy
      ? "「BaoBao dev」で API が起動しているか確認し、http://localhost:3001/api/health が開けるか試してください（localhost:5173 では Vite の /api プロキシ経由になります）。"
      : `VITE_API_BASE_URL=${configured} に API が届くか（同一 Wi‑Fi・ファイアウォール）を確認してください。`,
  ].join("");
}

function isAbortError(e: unknown): boolean {
  return e instanceof DOMException && e.name === "AbortError";
}

async function apiFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
  timeoutMs: number = API_FETCH_TIMEOUT_MS,
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } catch (e) {
    if (isAbortError(e)) {
      throw new Error(
        `API への接続が ${timeoutMs / 1000} 秒以内に完了しませんでした。${apiConnectionHint()}`,
      );
    }
    if (isLikelyNetworkFailure(e)) {
      throw new Error(apiConnectionHint());
    }
    throw e;
  } finally {
    clearTimeout(timer);
  }
}

async function parseJson(res: Response) {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return { error: text || "通信エラー" };
  }
}

function throwIfApiFailed(res: Response, data: Record<string, unknown>, fallback: string): void {
  if (res.ok) return;
  let msg = (typeof data.error === "string" && data.error) || fallback;
  if (res.status === 503 && typeof msg === "string" && msg.includes("Firebase Admin")) {
    msg += "（.env に FIREBASE_SERVICE_ACCOUNT_JSON か GOOGLE_APPLICATION_CREDENTIALS を設定し、npm run dev を再起動）";
  }
  throw new Error(msg);
}

async function authHeaders(idToken: string) {
  return {
    Authorization: `Bearer ${idToken}`,
    ...jsonHeaders,
  };
}

export type RegistrationConsents = {
  termsAccepted: boolean;
  privacyAccepted: boolean;
  personalDataAccepted: boolean;
  ageConfirmed: boolean;
  cameraUsageAccepted: boolean;
};

export async function profileInit(
  idToken: string,
  body: { name: string; consents?: RegistrationConsents; birthMonth?: number },
) {
  const res = await apiFetch(apiUrl("/api/profile/init"), {
    method: "POST",
    headers: await authHeaders(idToken),
    body: JSON.stringify(body),
  });
  const data = await parseJson(res);
  throwIfApiFailed(res, data, "プロフィールの作成に失敗しました");
  return data as { user: MemberUser };
}

export async function fetchMe(idToken: string) {
  const res = await apiFetch(apiUrl("/api/me"), {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  const data = await parseJson(res);
  throwIfApiFailed(res, data, "会員情報の取得に失敗しました");
  return data as { user: MemberUser };
}

export async function registerFcmToken(idToken: string, token: string, platform: string) {
  const res = await apiFetch(apiUrl("/api/me/fcm-token"), {
    method: "POST",
    headers: await authHeaders(idToken),
    body: JSON.stringify({ token, platform }),
  });
  const data = await parseJson(res);
  throwIfApiFailed(res, data, "プッシュ通知の登録に失敗しました");
  return data as { ok: true };
}

export async function unregisterFcmToken(idToken: string, token: string) {
  const res = await apiFetch(apiUrl("/api/me/fcm-token"), {
    method: "DELETE",
    headers: await authHeaders(idToken),
    body: JSON.stringify({ token }),
  });
  const data = await parseJson(res);
  throwIfApiFailed(res, data, "プッシュ通知の解除に失敗しました");
  return data as { ok: true };
}

export async function markBirthdayCouponUsed(idToken: string) {
  const res = await apiFetch(apiUrl("/api/me/coupons/birthday/mark-used"), {
    method: "POST",
    headers: await authHeaders(idToken),
    body: "{}",
  });
  const data = await parseJson(res);
  throwIfApiFailed(res, data, "クーポンの更新に失敗しました");
  return data as { user: MemberUser };
}

export type ApiHealth = {
  ok: boolean;
  firebaseAdmin?: boolean;
};

export async function fetchHealth(): Promise<ApiHealth> {
  const res = await apiFetch(apiUrl("/api/health"), undefined, HEALTH_FETCH_TIMEOUT_MS);
  const data = await parseJson(res);
  throwIfApiFailed(res, data, "API ヘルスチェックに失敗しました");
  return data as ApiHealth;
}

export async function fetchHistory(idToken: string) {
  const res = await apiFetch(apiUrl("/api/history"), {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  const data = await parseJson(res);
  throwIfApiFailed(res, data, "履歴の取得に失敗しました");
  return data as { history: StampHistoryItem[] };
}

export async function redeem(idToken: string, qrToken: string) {
  const res = await apiFetch(apiUrl("/api/redeem"), {
    method: "POST",
    headers: await authHeaders(idToken),
    body: JSON.stringify({ token: qrToken }),
  });
  const data = await parseJson(res);
  throwIfApiFailed(res, data, "スタンプの付与に失敗しました");
  return data as {
    ok: true;
    user: MemberUser;
    gainedPoints: number;
    stampsAdded: number;
  };
}

export type StaffIssueOptions = {
  /** true のとき QR に `publicOrigin/api/qr/{token}` を埋め込む（会員アプリの parse と整合） */
  embedUrl?: boolean;
  /** 例: window.location.origin */
  publicOrigin?: string;
  /** 店舗タブレット向け大きめ QR: 320 | 480 | 640 */
  qrWidth?: 320 | 480 | 640;
};

export async function staffIssueToken(staffKey: string, options?: StaffIssueOptions) {
  const body: Record<string, unknown> = {};
  if (options?.embedUrl) body.embedUrl = true;
  if (options?.publicOrigin) body.publicOrigin = options.publicOrigin;
  if (options?.qrWidth) body.qrWidth = options.qrWidth;

  const res = await apiFetch(apiUrl("/api/staff/issue-token"), {
    method: "POST",
    headers: {
      ...jsonHeaders,
      "X-Staff-Key": staffKey,
    },
    body: JSON.stringify(body),
  });
  const data = await parseJson(res);
  throwIfApiFailed(res, data, "トークン発行に失敗しました");
  return data as {
    token: string;
    expiresInMs: number;
    qrString: string;
    qrDataUrl: string;
  };
}
